import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const requirements = [
  { title: "Fire Safety Certificate", desc: "Shows proof of compliance with all fire safety provisions in the industrial building.", icon: "ri-file-text-line", link: "/resources/fsc-application-process" },
  { title: "Temporary Fire Permit", desc: "Issued for incomplete buildings allowing certain fire uses for approved purposes.", icon: "ri-time-line" },
  { title: "Performance Based Safety Design", desc: "Alternative compliance approach for buildings with unique design or operational needs.", icon: "ri-draft-line" },
  { title: "Emergency Response Plan", desc: "Documents emergency procedures for the building occupants and workers.", icon: "ri-map-2-line" },
  { title: "Chemical, Petroleum & Flammable Materials Licence & Permit", desc: "Required for premises storing or using flammable substances.", icon: "ri-flask-line" },
];

const guidelines = [
  {
    id: "worker-training",
    icon: "ri-graduation-cap-line",
    title: "Training Workers on Fire Safety",
    content:
      "Fire safety training for industry workers is critical to preventing fire emergencies. All the people working on the premises should be trained on how to detect and eliminate potential fire hazards in their workstations. They need to know about the common fire hazards in industrial spaces as well as those that can be accidentally caused by workers when performing different tasks. If the workers are trained on how to identify and eliminate potential fire threats, they will be able to maintain better fire safety standards at the workplace, thereby preventing frequent cases of industrial fires.",
  },
  {
    id: "protected-areas",
    icon: "ri-shield-check-line",
    title: "Keeping All Hazardous Materials and Assets in Protected Areas",
    content:
      "Depending on the particular production processes conducted on the premises, there are various kinds of hazardous or fire-causing elements found in industrial establishments like flammable liquids and huge electrical machines. To ensure that these materials do not accidentally start fires, they should be kept in protected areas. Besides, the use of hazardous machines should also be restricted to the protected areas. Leaving hazardous materials lying around is very risky since they can be easily mistaken and used inappropriately, causing fires. Accessibility to the protected areas should also be limited to only the people that are trained to work in those areas.",
  },
  {
    id: "hygiene-order",
    icon: "ri-building-line",
    title: "Maintaining Proper Hygiene, Order and Ventilation in Workspaces",
    content:
      "Due to the numerous fire risks in industrial spaces, you never know where or when a fire may start. As such, working in too crammed spaces with things thrown all over the place could only increase the chances of industrial fires. On the other hand, an orderly, spacious and well ventilated workspace enables you to easily identify any potential fire threats and remove them before an emergency occurs. All the work stations and equipment used therein should be kept clean and arranged in proper order. Besides, the premises should also have better ventilation to prevent accidental fires. That could also facilitate faster and safe evacuation of occupants from the premises during fire incidences.",
  },
  {
    id: "fire-regulations",
    icon: "ri-government-line",
    title: "Observe Fire Safety Regulations for Industrial Buildings",
    content:
      "According to the fire safety regulations, there are precise fire safety measures for industrial buildings that their owners or occupants must observe. Apart from the above guidelines, there are other basic regulations that every industrial building must meet for proper fire protection. Obtaining the required fire safety documents will surely give your workers and building the best defence against industrial fires.",
  },
  {
    id: "check-systems",
    icon: "ri-tools-line",
    title: "Check and Maintain Fire Protection and Electrical Systems Regularly",
    content:
      "The fire protection systems on your premises can only save lives and reduce destruction to assets if they are in proper condition whenever fires occur. Therefore, it is also recommended that all the systems should be thoroughly inspected and maintained on a regular basis. Maintaining highly efficient fire protection systems will ensure prompt detection and response to fires across all the workstations. A lot of industrial fires are also caused by faults in electrical systems and appliances. To keep such incidences at bay, you should also regularly check and maintain all the electrical systems and devices therein.",
  },
];

export default function PreventIndustrialFiresPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Prevent Industrial Fires Singapore",
            description:
              "Key guidelines for preventing industrial fires in Singapore, including worker training, hazardous material storage, workplace hygiene, fire safety regulations and system maintenance.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/6dc6ac34-3022-4a3f-a6a7-79576ea474df_compressed_Depositphotos_3527571_l-2015-min.webp"
              alt="Intense flames and fire blaze representing industrial fire hazards"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Prevent Industrial Fires</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Industrial Fire Safety
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              How to Prevent <span className="text-primary-400">Industrial Fires</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Industrial work areas usually have a lot of potential fire hazards. Industrial facilities should have fully effective and efficient fire prevention plans to ensure compliance and save lives.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                The Risks
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Industrial Fire Prevention is Critical
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Industrial work areas usually have a lot of potential fire hazards that pose significant dangers to the workers as well as the machines and other assets on the premises. As a result, industrial fires can be very destructive and result in enormous losses that might even put the company out of business.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                Industrial facilities should have fully effective and efficient fire prevention plans to ensure compliance with the fire safety regulations as well as save lives and minimize damage to assets during a fire emergency.
              </p>
              <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                  <i className="ri-lightbulb-line text-accent-600 text-lg"></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  Preventing industrial fires is a continuous and complex process that also requires professional support. Ezzogenics offers custom QP consultancy services to help you understand and choose the most suitable measures for preventing fires in your industrial establishment.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/b1cb103c-4a90-4384-a124-cdcb8356a5c5_compressed_Depositphotos_81126596_l-2015-min.webp"
                alt="Industrial fire-rated steel door with automatic closer mechanism"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Guidelines */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Key Guidelines
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Guidelines for Preventing <span className="text-primary-500">Industrial Fires</span>
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {guidelines.map((g, i) => (
                <div key={g.id} id={g.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${g.icon} text-primary-600 text-xl`}></i>
                      </div>
                      <span className="text-xs font-bold text-foreground-400">0{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${g.id}`}>{g.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed">{g.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fire Safety Requirements */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Compliance
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Main Fire Safety <span className="text-primary-500">Requirements for Industrial Buildings</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-2xl mx-auto">
              The main fire safety requirements for buildings that deal in industrial processes include the following documents and certifications.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {requirements.map((req) => (
              <div key={req.title} className="bg-background-100 rounded-2xl p-6 border border-background-200/70 flex flex-col gap-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 mb-1">
                  <i className={`${req.icon} text-accent-600 text-xl`}></i>
                </div>
                <h3 className="font-semibold text-foreground-900 text-base">{req.title}</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">{req.desc}</p>
                {req.link && (
                  <Link
                    to={req.link}
                    className="inline-flex items-center gap-1.5 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors mt-auto pt-1"
                  >
                    Learn the full application process
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Get Expert Industrial Fire Safety Solutions
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
              Ezzogenics offers custom QP consultancy services to help you understand and choose the most suitable measures for preventing fires in your industrial establishment.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Get QP Consultancy
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                View Our Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}